import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import { logEvent } from "firebase/analytics";
import { analytics } from "config/firebase.js";

import {
  login,
  logout,
  registerUser,
  registerUserWithGoogle,
  registerUserWithFacebook,
  observer,
  sendResetPasswordEmail,
  getCurrentUserData,
} from "api/auth.api";

const useAuth = () => {
  const router = useRouter();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    setIsLoading(true);
    const unsubscribe = observer((user) => {
      if (user) {
        getCurrentUserData(user.uid).then((data) => {
          setCurrentUser({ ...data, ...user });
        });
        setIsLoading(false);
        logEvent(analytics, "user_status", {
          status: "registered",
          uid: user.uid,
          email: user.email || "unknown user",
          displayName: user.displayName || "unknown user",
        });
      } else {
        setCurrentUser(null);
        setIsLoading(false);
      }
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    return logout().then(() =>
      router.push("/auth?mode=login", { replace: true }),
    );
  }, [router]);

  const loginWithEmailAndPassword = useCallback(
    async (email, password) => {
      return login(email, password).then(() => router.push("/"));
    },
    [router],
  );

  const register = useCallback(
    async (email, password) => {
      return registerUser(email, password).then(() => router.push("/"));
    },
    [router],
  );

  const loginWithGoogle = useCallback(async () => {
    return registerUserWithGoogle().then(() => router.push("/"));
  }, [router]);

  const loginWithFacebook = useCallback(async () => {
    return registerUserWithFacebook().then(() => router.push("/"));
  }, [router]);

  const sendPasswordResetEmail = useCallback(
    async (email) => {
      return sendResetPasswordEmail(email).then(() => router.push("/"));
    },
    [router],
  );

  return {
    currentUser,
    isLoading,
    loginWithEmailAndPassword,
    loginWithGoogle,
    loginWithFacebook,
    register,
    signOut,
    sendPasswordResetEmail,
    setCurrentUser,
  };
};

export default useAuth;
